import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProductsAPI } from '@/api';
import { formatApiError } from '@/utils';
import { IApiError } from '@/models/apiError.model';
import { PRODUCTS_SLICE_NAME } from './models';
import {
  IProductListResponse,
  IGetProductsListDTO,
  IDeleteBulkProductsDTO,
  IToggleActiveBulkProductDTO,
  IGetProductResponse,
  IUpdateProductMainInfoDTO,
  IGetFiltersByCategoryResponse,
  IGetProductVariationsResponse,
  IUpdateProductVariationDto,
  IAddProductVariationsBulkDTO,
  IProductVariation,
  IUpdateProductSeoDTO,
  IGetProductRegionsAndPricesResponse,
  IUpdateRegionsAndPricesItemDTO,
  IUpdateRegionsAndPricesItemResponse,
  IBulkUpdateStocksDTO,
  IBulkUpdateStocksResponse,
  IGetProductsOfMonthListDTO,
  IUpdateProductsOfMonthDTO,
  IStoreResponse,
  IGetStoreListDTO,
  ICreateProductDTO,
  ITopProductListResponse,
  IGetTopProductResponse,
  IUpdateTopProductsDTO,
  IReorderTopProductsDTO,
} from '@/models/products.model';

export const getProductsList = createAsyncThunk<
  IProductListResponse,
  IGetProductsListDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getProductsList`,
  async (data) => {
    const response = await ProductsAPI.getProductsList(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getStoresList = createAsyncThunk<IStoreResponse, IGetStoreListDTO, { serializedErrorType: IApiError }>(
  `${PRODUCTS_SLICE_NAME}/getStoresList`,
  async (data) => {
    const response = await ProductsAPI.getStoresList(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getProductsOfMonthList = createAsyncThunk<
  IProductListResponse,
  IGetProductsOfMonthListDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getProductsOfMonthList`,
  async (data) => {
    const response = await ProductsAPI.getProductsOfMonthList(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getTopProductList = createAsyncThunk<
  ITopProductListResponse,
  undefined,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getTopProductList`,
  async () => {
    const response = await ProductsAPI.getTopProductList();
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateProductsOfMonth = createAsyncThunk<
  IGetProductResponse,
  { id: string; params: IUpdateProductsOfMonthDTO },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/updateProductsOfMonth`,
  async ({ id, params }) => {
    const response = await ProductsAPI.updateProductsOfMonth({ id, params });
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateTopProduct = createAsyncThunk<
  IGetTopProductResponse,
  { id: string; params: IUpdateTopProductsDTO },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/updateTopProduct`,
  async ({ id, params }) => {
    const response = await ProductsAPI.updateTopProduct({ id, params });
    return response.data;
  },
  { serializeError: formatApiError },
);

export const createProductsOfMonth = createAsyncThunk<
  IGetProductResponse,
  IUpdateProductsOfMonthDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/createProductsOfMonth`,
  async (data) => {
    const response = await ProductsAPI.createProductsOfMonth(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const createTopProduct = createAsyncThunk<
  IGetTopProductResponse,
  IUpdateTopProductsDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/createTopProduct`,
  async (data) => {
    const response = await ProductsAPI.createTopProduct(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const reorderTopProduct = createAsyncThunk<
  IGetTopProductResponse,
  IReorderTopProductsDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/reorderTopProduct`,
  async (data) => {
    const response = await ProductsAPI.reorderTopProduct(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteProductsOfMonth = createAsyncThunk<undefined, string, { serializedErrorType: IApiError }>(
  `${PRODUCTS_SLICE_NAME}/deleteProductsOfMonth`,
  async (id) => {
    const response = await ProductsAPI.deleteProductsOfMonth(id);
    return response.data?.data;
  },
  { serializeError: formatApiError },
);

export const deleteTopProducts = createAsyncThunk<undefined, string, { serializedErrorType: IApiError }>(
  `${PRODUCTS_SLICE_NAME}/deleteTopProducts`,
  async (id) => {
    const response = await ProductsAPI.deleteTopProducts(id);
    return response.data?.data;
  },
  { serializeError: formatApiError },
);

export const getProduct = createAsyncThunk<IGetProductResponse, string, { serializedErrorType: IApiError }>(
  `${PRODUCTS_SLICE_NAME}/getProduct`,
  async (data) => {
    const response = await ProductsAPI.getProduct(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getFiltersByCategory = createAsyncThunk<
  IGetFiltersByCategoryResponse,
  string,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getFiltersByCategory`,
  async (categoryId) => {
    const response = await ProductsAPI.getFiltersByCategory(categoryId);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const getProductFiltersByCategory = createAsyncThunk<
  IGetFiltersByCategoryResponse,
  { productId: string; categoryId: string },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getProductFiltersByCategory`,
  async (data) => {
    const response = await ProductsAPI.getProductFiltersByCategory(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateProductFilters = createAsyncThunk<
  IGetProductResponse,
  { productId: string; params: Record<string, string> },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/updateProductFilters`,
  async (data) => {
    const response = await ProductsAPI.updateProductFilters(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateProduct = createAsyncThunk<
  IGetProductResponse,
  { id: string; data: IUpdateProductMainInfoDTO },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/updateProduct`,
  async ({ id, data }) => {
    const response = await ProductsAPI.updateProduct({ id, data });
    return response.data;
  },
  { serializeError: formatApiError },
);

export const createProduct = createAsyncThunk<
  IGetProductResponse,
  ICreateProductDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/createProduct`,
  async (data) => {
    const response = await ProductsAPI.createProduct(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateProductSeo = createAsyncThunk<
  IGetProductResponse,
  { id: string; data: IUpdateProductSeoDTO },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/updateProductSeo`,
  async ({ id, data }) => {
    const response = await ProductsAPI.updateProductSeo({ id, data });
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteProduct = createAsyncThunk<undefined, string, { serializedErrorType: IApiError }>(
  `${PRODUCTS_SLICE_NAME}/deleteProduct`,
  async (id) => {
    const response = await ProductsAPI.deleteProduct(id);
    return response.data?.data;
  },
  { serializeError: formatApiError },
);

export const deleteBulkProduct = createAsyncThunk<
  undefined,
  IDeleteBulkProductsDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/deleteProduct`,
  async (data) => {
    const response = await ProductsAPI.deleteBulkProduct(data);
    return response.data?.data;
  },
  { serializeError: formatApiError },
);

export const toggleActiveBulkProduct = createAsyncThunk<
  undefined,
  IToggleActiveBulkProductDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/toggleActiveBulkProduct`,
  async (data) => {
    const response = await ProductsAPI.toggleActiveBulkProduct(data);
    return response.data?.data;
  },
  { serializeError: formatApiError },
);

export const getProductVariations = createAsyncThunk<
  IGetProductVariationsResponse,
  string,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getProductVariations`,
  async (data) => {
    const response = await ProductsAPI.getProductVariations(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const deleteProductVariation = createAsyncThunk<string, string, { serializedErrorType: IApiError }>(
  `${PRODUCTS_SLICE_NAME}/deleteProductVariation`,
  async (data) => {
    await ProductsAPI.deleteProductVariation(data);
    return data;
  },
  { serializeError: formatApiError },
);

export const addProductVariation = createAsyncThunk<
  IProductVariation,
  IUpdateProductVariationDto,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/addProductVariation`,
  async (data) => {
    await ProductsAPI.addProductVariation(data);
    return data.variation;
  },
  { serializeError: formatApiError },
);

export const addProductVariationsBulk = createAsyncThunk<
  string[],
  IAddProductVariationsBulkDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/addProductVariationsBulk`,
  async (data) => {
    await ProductsAPI.addProductVariationsBulk(data);
    return data.variation_ids;
  },
  { serializeError: formatApiError },
);

export const getProductRegionsAndPrices = createAsyncThunk<
  IGetProductRegionsAndPricesResponse,
  string,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/getProductRegionsAndPrices`,
  async (data) => {
    const response = await ProductsAPI.getProductRegionsAndPrices(data);
    return response.data;
  },
  { serializeError: formatApiError },
);

export const updateRegionsAndPricesItem = createAsyncThunk<
  IUpdateRegionsAndPricesItemResponse,
  { id: string; product_id: string; data: IUpdateRegionsAndPricesItemDTO },
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/updateRegionsAndPricesItem`,
  async ({ id, product_id, data }) => {
    const response = await ProductsAPI.updateRegionsAndPricesItem({ id, product_id, data });
    return response.data;
  },
  { serializeError: formatApiError },
);

export const bulkUpdateRegionsAndPrices = createAsyncThunk<
  IBulkUpdateStocksResponse,
  IBulkUpdateStocksDTO,
  { serializedErrorType: IApiError }
>(
  `${PRODUCTS_SLICE_NAME}/bulkUpdateRegionsAndPrices`,
  async (data) => {
    const response = await ProductsAPI.bulkUpdateStocks(data);
    return response.data;
  },
  { serializeError: formatApiError },
);
